// @flow
import * as React from 'react';
import { Link } from 'react-router';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import type { Organisation } from '../../types';

type Props = {
  currentOrganisation: Organisation,
  params: {
    customerId: any
  },
  getOrganisationUsers: Function,
  removeUserFromOrganisation: Function,
  route: any,
  router: any,
  currentCustomer: Organisation
};

type State = {
  newUserId?: ?any,
  showNewUser?: boolean,
  filterValue: string,
  isSaving?: boolean,
  errorMsg?: any,
  successMsg?: any
};

const ControlHeader = props => (
  <Row className="row-header tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>
    <Col sm={5} className="table-header" />
    <Col sm={3} className="table-header">
      <Button
        bsStyle="primary"
        type="submit"
        className="relative-padding"
        onClick={props.addButtonClick}
      >
        Add User
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const { currentCustomer, item, removeUser } = props;
  return (
    <Row key={item.id}>
      <Col sm={5} className="table-entry">
        <Link to={`/customer/${currentCustomer.id}/users/${item.id}`}>
          {item.name}
        </Link>
      </Col>
      <Col sm={2} className="table-entry">
        {item.isArchived ? 'Archived' : 'Active'}
      </Col>
      <Col sm={3} className="table-entry text-center">
        {item.isSuperUser ? 'Yes' : 'No'}
      </Col>
      <Col sm={2} className="table-entry">
        {item.userOrganisationId && (
          <Button
            bsStyle="link"
            onClick={e => removeUser(item)}
            aria-label={`Remove ${item.name}`}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
};

class CustomerUsers extends React.Component<Props, State> {
  timeoutId: any;
  fuzzyFilterFactory: any;
  screenReaderContent: any;
  static displayName = 'CustomerUsers';

  constructor(props: Props) {
    super(props);

    this.state = {
      filterValue: ''
    };
    this.timeoutId = undefined;
    this.fuzzyFilterFactory = fuzzyFilterFactory();
  }

  componentDidMount() {
    this.getUsers();
    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getUsers = () => {
    const organisationId = parseInt(this.props.params.customerId, 10);
    this.props.getOrganisationUsers(organisationId, true);
    this.props.getOrganisationUsers(organisationId, false);
  };

  removeUser = (user: any) => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const organisationId = parseInt(this.props.params.customerId, 10);
    this.props.removeUserFromOrganisation(user, organisationId).then(
      response => {
        this.getUsers();
        this.success(response);
      },
      error => this.failure(error)
    );
  };

  success = (response: any) => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = (error: any) => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  addButtonClick = () => {
    const { customerId } = this.props.params;
    const context = this.props.route.context;
    const path = context === 'global' ? '/customers' : '/customer';
    localStorage.setItem('backUrl', `${path}/${customerId}/userList`);
    this.props.router.push(`/customer/${customerId}/users/invite`);
  };

  updateFilterValue = (filterValue: any) => {
    this.setState({ filterValue: filterValue || '' });
  };

  render() {
    const { currentCustomer } = this.props;
    const { allocated } = currentCustomer.users;

    const LineItemWrapper = props => (
      <LineItem
        {...props}
        currentCustomer={currentCustomer}
        removeUser={this.removeUser}
      />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing users for customer {this.props.currentCustomer.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />
        <Row>
          <Col sm={5} className="table-header">
            Name
          </Col>
          <Col sm={2} className="table-header">
            Status
          </Col>
          <Col sm={3} className="table-header text-center">
            Can access all stores?
          </Col>
          <Col sm={2} className="table-header text-center">
            Actions
          </Col>
        </Row>
        <hr />

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="users"
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default CustomerUsers;



// WEBPACK FOOTER //
// ./src/containers/customer/CustomerUsers.js